<template>
  <div class="contract-editor-wrapper" :class="{ 'contract-editor-embedded': embedded }">
    <vue-editor
      v-if="embedded"
      ref="editor"
      v-model="content"
      :editorOptions="editorSettings"
      :editorToolbar="customToolbar"
      @ready="onEditorReady"
      @text-change="onContentChange"
      @selection-change="onSelectionChange"
    ></vue-editor>

    <template v-else>
      <div class="document-canvas" :style="{ maxHeight: canvasMaxHeight }">
        <div class="document-page">
          <vue-editor
            ref="editor"
            v-model="content"
            :editorOptions="editorSettings"
            :editorToolbar="customToolbar"
            @ready="onEditorReady"
            @text-change="onContentChange"
            @selection-change="onSelectionChange"
          ></vue-editor>
        </div>
      </div>

      <div class="editor-status-bar">
        <div class="d-flex justify-content-between align-items-center">
          <div class="d-flex gap-3">
            <span><AppIcon name="bi-fonts" class="bi bi-fonts me-1" />{{ wordCount }} {{ $t('words') }}</span>
            <span><AppIcon name="bi-type" class="bi bi-type me-1" />{{ charCount }} {{ $t('characters') || 'characters' }}</span>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <span class="text-muted small">{{ $t('zoom') || 'Zoom' }}:</span>
            <button class="btn btn-sm btn-link p-0 text-muted" @click="adjustZoom(-10)"><AppIcon name="bi-dash" class="bi bi-dash" /></button>
            <span class="small" style="min-width: 40px; text-align: center;">{{ zoomLevel }}%</span>
            <button class="btn btn-sm btn-link p-0 text-muted" @click="adjustZoom(10)"><AppIcon name="bi-plus" class="bi bi-plus" /></button>
            <button class="btn btn-sm btn-link p-0 text-muted" @click="zoomLevel = 100"><AppIcon name="bi-arrow-counterclockwise" class="bi bi-arrow-counterclockwise" /></button>
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="variableAutocompleteOpen"
      class="smart-variable-autocomplete"
      :style="variableAutocompleteStyle"
      role="listbox"
      :aria-label="$t('smart_variables')"
    >
      <button
        v-for="(suggestion, index) in variableSuggestions"
        :key="`${suggestion.insertKey}-${index}`"
        type="button"
        class="smart-variable-suggestion"
        :class="{ active: index === activeSuggestionIndex }"
        role="option"
        :aria-selected="index === activeSuggestionIndex ? 'true' : 'false'"
        @mouseenter="activeSuggestionIndex = index"
        @mousedown.prevent="selectVariableSuggestion(suggestion)"
      >
        <span>{{ suggestion.label }}</span>
        <code>{{ suggestion.insertKey }}</code>
      </button>
    </div>
  </div>
</template>

<script>
import { VueEditor, Quill } from "vue2-editor";

// Expose Quill to window for the image resize module
window.Quill = Quill;
// Use require to prevent hoisting issues and ensure window.Quill is set first
let ImageResize = require("quill-image-resize-module");
// Handle different export formats (some builds imply .default)
ImageResize = ImageResize.default || ImageResize;

Quill.register("modules/imageResize", ImageResize);

// Register custom fonts
const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'times-new-roman', 'courier-new', 'georgia', 'verdana', 'tahoma', 'trebuchet-ms', 'impact', 'comic-sans-ms'];
Quill.register(Font, true);

// Register custom font sizes
const Size = Quill.import('attributors/style/size');
Size.whitelist = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px', '72px'];
Quill.register(Size, true);

const Inline = Quill.import('blots/inline');
class SmartVariableBlot extends Inline {}
SmartVariableBlot.blotName = 'smart-variable';
SmartVariableBlot.tagName = 'span';
SmartVariableBlot.className = 'smart-variable-token';
Quill.register(SmartVariableBlot, true);

export default {
  name: "ContractEditor",
  components: {
    VueEditor
  },
  props: {
    value: {
      type: String,
      default: ""
    },
    variables: {
      type: Array,
      default: () => []
    },
    pagePadding: {
      type: [Number, String],
      default: 20
    },
    pageMargin: {
      type: [Number, String],
      default: 20
    },
    editorMinHeight: {
      type: [Number, String],
      default: 1100
    },
    canvasMaxHeight: {
      type: String,
      default: '70vh'
    },
    embedded: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      content: this.value,
      selectedVariable: "",
      zoomLevel: 100,
      wordCount: 0,
      charCount: 0,
      variableAutocompleteOpen: false,
      variableSuggestions: [],
      activeSuggestionIndex: 0,
      autocompleteRange: null,
      variableAutocompleteStyle: {
        left: '0px',
        top: '0px'
      },
      customToolbar: [
        [{ 'font': ['', 'arial', 'times-new-roman', 'courier-new', 'georgia', 'verdana', 'tahoma', 'trebuchet-ms'] }],
        [{ 'size': ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px', '72px'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
        [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }],
        [{ 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        ['blockquote'],
        ['link', 'image', 'video'],
        ['clean']
      ],
      editorSettings: {
        modules: {
          imageResize: {},
          toolbar: {
            container: [
              [{ 'font': ['', 'arial', 'times-new-roman', 'courier-new', 'georgia', 'verdana', 'tahoma', 'trebuchet-ms'] }],
              [{ 'size': ['8px', '10px', '12px', '14px', '16px', '18px', '20px', '24px', '28px', '32px', '36px', '48px', '64px', '72px'] }],
              ['bold', 'italic', 'underline', 'strike'],
              [{ 'color': [] }, { 'background': [] }],
              [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }, { 'align': 'justify' }],
              [{ 'header': 1 }, { 'header': 2 }, { 'header': 3 }],
              [{ 'list': 'ordered' }, { 'list': 'bullet' }],
              [{ 'indent': '-1' }, { 'indent': '+1' }],
              [{ 'script': 'sub' }, { 'script': 'super' }],
              ['blockquote'],
              ['link', 'image', 'video'],
              ['clean']
            ]
          }
        }
      }
    };
  },
  watch: {
    value(val) {
      if (val !== this.content) {
        this.content = val;
      }
      this.syncEditorContent(val);
    },
    content() {
      this.updateWordCount();
    },
    pagePadding() { this.updateEditorStyle(); },
    pageMargin() { this.updateEditorStyle(); },
    editorMinHeight() { this.updateEditorStyle(); },
    zoomLevel() { this.updateZoom(); }
  },
  created() {
    // Register Padding and Margin Attributors
    const Parchment = Quill.import("parchment");
    const PaddingStyle = new Parchment.Attributor.Style("padding", "padding", {
      scope: Parchment.Scope.BLOCK
    });
    const MarginStyle = new Parchment.Attributor.Style("margin", "margin", {
      scope: Parchment.Scope.BLOCK
    });
    Quill.register(PaddingStyle, true);
    Quill.register(MarginStyle, true);
  },
  mounted() {
    this.updateEditorStyle();
    this.updateWordCount();
    this.updateZoom();

    // Set custom font labels after toolbar renders
    this.$nextTick(() => {
      this.setFontLabels();
      this.setSizeLabels();
    });
  },
  beforeDestroy() {
    if (this._quill && this._variableKeydownHandler) {
      this._quill.root.removeEventListener('keydown', this._variableKeydownHandler, true);
    }
    this.restoreToolbar();
  },
  methods: {
    onEditorReady(quill) {
      this._quill = quill;
      this._variableKeydownHandler = this.handleVariableAutocompleteKeydown;
      quill.root.addEventListener('keydown', this._variableKeydownHandler, true);
      this.syncEditorContent(this.value, true);
    },
    syncEditorContent(value, force = false) {
      const quill = this._quill || (this.$refs.editor && this.$refs.editor.quill);
      if (!quill || !quill.root) return;

      const html = value || '';
      const currentHtml = quill.root.innerHTML === '<p><br></p>' ? '' : quill.root.innerHTML;
      if (!force && currentHtml === html) return;

      this._hydratingContent = true;
      quill.root.innerHTML = html || '<p><br></p>';
      if (typeof quill.update === 'function') {
        quill.update(Quill.sources.SILENT);
      }
      this.content = html;
      this.$nextTick(() => {
        this._hydratingContent = false;
        this.updateEditorStyle();
      });
    },
    getToolbarElement() {
      if (this._toolbarElement) return this._toolbarElement;

      const toolbar = this.$el.querySelector('.ql-toolbar.ql-snow');
      if (!toolbar) return null;

      this._toolbarElement = toolbar;
      this._toolbarHome = {
        parent: toolbar.parentNode,
        nextSibling: toolbar.nextSibling
      };
      return toolbar;
    },
    dockToolbar(target) {
      if (!target) return false;
      const toolbar = this.getToolbarElement();
      if (!toolbar) return false;

      if (toolbar.parentNode !== target) target.appendChild(toolbar);
      toolbar.classList.add('contract-quill-toolbar-docked');
      return true;
    },
    restoreToolbar() {
      const toolbar = this._toolbarElement;
      const home = this._toolbarHome;
      if (!toolbar || !home || !home.parent) return;

      toolbar.classList.remove('contract-quill-toolbar-docked');
      if (toolbar.parentNode === home.parent) return;

      if (home.nextSibling && home.nextSibling.parentNode === home.parent) {
        home.parent.insertBefore(toolbar, home.nextSibling);
      } else {
        home.parent.appendChild(toolbar);
      }
    },
    updateEditorStyle() {
        this.$nextTick(() => {
            const editorEl = this.$el.querySelector('.ql-editor');
            if (editorEl) {
                const padding = this.embedded ? 0 : this.pagePadding;
                const margin = this.embedded ? 0 : this.pageMargin;
                editorEl.style.setProperty('padding', `${padding}px`, 'important');
                editorEl.style.margin = `${margin}px`;
                editorEl.style.minHeight = `${this.editorMinHeight}px`;
            }
        });
    },
    updateZoom() {
      if (this.embedded) return;
      this.$nextTick(() => {
        const page = this.$el.querySelector('.document-page');
        if (page) {
          page.style.transform = `scale(${this.zoomLevel / 100})`;
          page.style.transformOrigin = 'top center';
        }
      });
    },
    adjustZoom(delta) {
      const newZoom = this.zoomLevel + delta;
      if (newZoom >= 50 && newZoom <= 200) {
        this.zoomLevel = newZoom;
      }
    },
    updateWordCount() {
      if (!this.content) {
        this.wordCount = 0;
        this.charCount = 0;
        return;
      }
      // Strip HTML tags for counting
      const text = this.content.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
      this.charCount = text.length;
      this.wordCount = text.length > 0 ? text.split(/\s+/).filter(w => w.length > 0).length : 0;
    },
    onContentChange() {
      if (this._hydratingContent) return;
      this.$emit("input", this.content);
      this.$nextTick(() => this.updateVariableAutocomplete());
    },
    onSelectionChange(range) {
      if (range) {
        this.$emit('focus');
        this.$nextTick(() => this.updateVariableAutocomplete());
      } else {
        this.closeVariableAutocomplete();
      }
    },
    insertVariable() {
      if (!this.selectedVariable) return;
      
      const quill = this.$refs.editor.quill;
      const range = quill.getSelection(true);
      this.insertSmartVariableToken(this.selectedVariable, range.index, 0);
      
      this.selectedVariable = "";
    },
    updateVariableAutocomplete() {
      const quill = this._quill || (this.$refs.editor && this.$refs.editor.quill);
      if (!quill || !quill.hasFocus()) {
        this.closeVariableAutocomplete();
        return;
      }

      const range = quill.getSelection();
      if (!range || range.length) {
        this.closeVariableAutocomplete();
        return;
      }

      const textBeforeCursor = quill.getText(0, range.index);
      const match = textBeforeCursor.match(/(?:^|[\s(,;:])([a-zA-Z][a-zA-Z0-9_.]*)$/);
      const query = match ? match[1].toLowerCase() : '';
      if (query.length < 2) {
        this.closeVariableAutocomplete();
        return;
      }

      const suggestions = this.buildVariableSuggestions(query).slice(0, 6);
      if (!suggestions.length) {
        this.closeVariableAutocomplete();
        return;
      }

      this.variableSuggestions = suggestions;
      this.activeSuggestionIndex = Math.min(this.activeSuggestionIndex, suggestions.length - 1);
      this.autocompleteRange = { index: range.index - match[1].length, length: match[1].length };
      this.positionVariableAutocomplete(quill, range.index);
      this.variableAutocompleteOpen = true;
    },
    buildVariableSuggestions(query) {
      const preferredAlias = ['client', 'customer', 'supplier', 'employee']
        .find(prefix => query.startsWith(prefix));

      return this.variables
        .map(variable => {
          const canonical = String(variable.key || '').replace(/[{}]/g, '').trim();
          const field = canonical.split('.').pop();
          const namespace = canonical.split('.')[0];
          const aliases = namespace === 'entity'
            ? ['client', 'customer', 'supplier', 'employee'].map(prefix => `${prefix}.${field}`)
            : [];
          const searchValues = [canonical, variable.label || '', ...aliases].map(value => value.toLowerCase());
          const score = searchValues.some(value => value.startsWith(query)) ? 0 : 1;
          const insertNamespace = namespace === 'entity' && preferredAlias ? preferredAlias : namespace;
          const insertKey = `{{${insertNamespace}.${field}}}`;
          const labelPrefix = insertNamespace.charAt(0).toUpperCase() + insertNamespace.slice(1);
          const label = namespace === 'entity' && preferredAlias
            ? `${labelPrefix} ${String(field || '').replace(/_/g, ' ')}`
            : (variable.label || canonical);
          return { insertKey, label, score };
        })
        .filter(suggestion => {
          const searchable = `${suggestion.label} ${suggestion.insertKey}`.toLowerCase();
          return searchable.includes(query) || suggestion.score === 0;
        })
        .sort((left, right) => left.score - right.score || left.label.localeCompare(right.label));
    },
    positionVariableAutocomplete(quill, index) {
      const bounds = quill.getBounds(index);
      const container = quill.container;
      this.variableAutocompleteStyle = {
        left: `${container.offsetLeft + bounds.left}px`,
        top: `${container.offsetTop + bounds.top + bounds.height + 6}px`
      };
    },
    handleVariableAutocompleteKeydown(event) {
      if (!this.variableAutocompleteOpen || !this.variableSuggestions.length) return;
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.activeSuggestionIndex = (this.activeSuggestionIndex + 1) % this.variableSuggestions.length;
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.activeSuggestionIndex = (this.activeSuggestionIndex - 1 + this.variableSuggestions.length) % this.variableSuggestions.length;
      } else if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        this.selectVariableSuggestion(this.variableSuggestions[this.activeSuggestionIndex]);
      } else if (event.key === 'Escape') {
        event.preventDefault();
        this.closeVariableAutocomplete();
      }
    },
    selectVariableSuggestion(suggestion) {
      if (!suggestion || !this.autocompleteRange) return;
      this.insertSmartVariableToken(
        suggestion.insertKey,
        this.autocompleteRange.index,
        this.autocompleteRange.length
      );
    },
    insertSmartVariableToken(variableKey, index, replaceLength = 0) {
      const quill = this._quill || (this.$refs.editor && this.$refs.editor.quill);
      if (!quill) return;
      if (replaceLength) quill.deleteText(index, replaceLength, Quill.sources.USER);
      quill.insertText(index, variableKey, { 'smart-variable': true }, Quill.sources.USER);
      const nextIndex = index + variableKey.length;
      quill.setSelection(nextIndex, 0, Quill.sources.SILENT);
      quill.format('smart-variable', false, Quill.sources.SILENT);
      this.closeVariableAutocomplete();
      quill.focus();
    },
    closeVariableAutocomplete() {
      this.variableAutocompleteOpen = false;
      this.variableSuggestions = [];
      this.activeSuggestionIndex = 0;
      this.autocompleteRange = null;
    },
    focusAtEnd() {
      const editor = this.$refs.editor;
      if (!editor || !editor.quill) return;
      const quill = editor.quill;
      const index = Math.max(0, quill.getLength() - 1);
      quill.focus();
      quill.setSelection(index, 0);
    },
    setFontLabels() {
      const fontLabels = {
        '': 'Default',
        'arial': 'Arial',
        'times-new-roman': 'Times New Roman',
        'courier-new': 'Courier New',
        'georgia': 'Georgia',
        'verdana': 'Verdana',
        'tahoma': 'Tahoma',
        'trebuchet-ms': 'Trebuchet MS'
      };
      const fontPicker = this.$el.querySelector('.ql-font .ql-picker-label');
      if (fontPicker) {
        const items = this.$el.querySelectorAll('.ql-font .ql-picker-item');
        items.forEach(item => {
          const value = item.getAttribute('data-value') || '';
          if (fontLabels[value]) {
            item.textContent = fontLabels[value];
          }
        });
      }
    },
    setSizeLabels() {
      const items = this.$el.querySelectorAll('.ql-size .ql-picker-item');
      items.forEach(item => {
        const value = item.getAttribute('data-value') || '';
        if (value) {
          item.textContent = value.replace('px', 'pt');
        }
      });
    }
  }
};
</script>

<style>
/* ===== Contract Editor Wrapper ===== */
.contract-editor-wrapper {
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  background: #f3f4f6;
}

.smart-variable-autocomplete {
  position: absolute;
  z-index: 100;
  display: grid;
  width: min(310px, calc(100% - 16px));
  max-height: 230px;
  padding: 5px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #dfe3eb;
  border-radius: 9px;
  box-shadow: 0 12px 30px rgba(31, 38, 55, 0.18);
}

.smart-variable-suggestion {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  padding: 7px 9px;
  color: #3f4756;
  font-size: 0.75rem;
  text-align: left;
  background: transparent;
  border: 0;
  border-radius: 6px;
}

.smart-variable-suggestion:hover,
.smart-variable-suggestion.active {
  color: #303030;
  background: #fff4db;
}

.smart-variable-suggestion code {
  overflow: hidden;
  color: #a45b00;
  font-size: 0.68rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contract-editor-wrapper .ql-editor .smart-variable-token {
  display: inline;
  padding: 2px 4px;
  color: #9a5600;
  font-weight: 600;
  background: #ffedd0;
  border: 1px solid #f4c77a;
  border-radius: 4px;
  box-decoration-break: clone;
  -webkit-box-decoration-break: clone;
}

.contract-editor-wrapper.contract-editor-embedded {
  border: 0;
  border-radius: 0;
  overflow: visible;
  background: transparent;
}

/* ===== Toolbar Area ===== */
.editor-toolbar-area {
  padding: 8px 12px;
  background: #ffffff;
  border-bottom: 1px solid #e5e7eb;
}

/* ===== Quill Toolbar Styling ===== */
.contract-editor-wrapper .ql-toolbar.ql-snow {
  border: none !important;
  border-bottom: 1px solid #e5e7eb !important;
  background: #ffffff;
  padding: 6px 8px;
  position: sticky;
  top: 0;
  z-index: 10;
}

.contract-editor-wrapper .ql-toolbar .ql-picker {
  font-size: 13px;
}

.contract-editor-wrapper .ql-toolbar .ql-font {
  width: 150px !important;
}

.contract-editor-wrapper .ql-toolbar .ql-size {
  width: 70px !important;
}

.contract-editor-wrapper .ql-toolbar button {
  width: 28px;
  height: 28px;
  padding: 3px;
  border-radius: 4px;
}

.contract-editor-wrapper .ql-toolbar button:hover {
  background: #f0f0f0;
}

.contract-editor-wrapper .ql-toolbar button.ql-active {
  background: #e8f0fe;
  color: #1a73e8;
}

/* ===== Document Canvas (Gray background like Word) ===== */
.document-canvas {
  background: #e8e8e8;
  padding: 30px 20px;
  overflow-y: auto;
  max-height: 70vh;
  min-height: 500px;
}

/* ===== Document Page (A4 simulation) ===== */
.document-page {
  max-width: 210mm;
  margin: 0 auto;
  background: #ffffff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05);
  border-radius: 2px;
  transition: transform 0.2s ease;
}

/* ===== Quill Editor Container ===== */
.contract-editor-wrapper .ql-container.ql-snow {
  border: none !important;
  font-family: 'Times New Roman', Times, serif;
  font-size: 14px;
}

.contract-editor-wrapper .ql-editor {
  min-height: 1100px;
  padding: 40px 50px !important;
  line-height: 1.6;
  color: #1a1a1a;
  background-color: white;
}

.contract-editor-wrapper .ql-editor:focus {
  outline: none;
}

/* ===== Font Family Definitions ===== */
.ql-font-arial { font-family: Arial, Helvetica, sans-serif; }
.ql-font-times-new-roman { font-family: 'Times New Roman', Times, serif; }
.ql-font-courier-new { font-family: 'Courier New', Courier, monospace; }
.ql-font-georgia { font-family: Georgia, serif; }
.ql-font-verdana { font-family: Verdana, Geneva, sans-serif; }
.ql-font-tahoma { font-family: Tahoma, Geneva, sans-serif; }
.ql-font-trebuchet-ms { font-family: 'Trebuchet MS', Helvetica, sans-serif; }
.ql-font-impact { font-family: Impact, Charcoal, sans-serif; }
.ql-font-comic-sans-ms { font-family: 'Comic Sans MS', cursive, sans-serif; }

/* Font picker labels */
.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="arial"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="arial"]::before { content: 'Arial' !important; font-family: Arial, sans-serif; }

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="times-new-roman"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="times-new-roman"]::before { content: 'Times New Roman' !important; font-family: 'Times New Roman', serif; }

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="courier-new"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="courier-new"]::before { content: 'Courier New' !important; font-family: 'Courier New', monospace; }

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="georgia"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="georgia"]::before { content: 'Georgia' !important; font-family: Georgia, serif; }

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="verdana"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="verdana"]::before { content: 'Verdana' !important; font-family: Verdana, sans-serif; }

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="tahoma"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="tahoma"]::before { content: 'Tahoma' !important; font-family: Tahoma, sans-serif; }

.ql-snow .ql-picker.ql-font .ql-picker-label[data-value="trebuchet-ms"]::before,
.ql-snow .ql-picker.ql-font .ql-picker-item[data-value="trebuchet-ms"]::before { content: 'Trebuchet MS' !important; font-family: 'Trebuchet MS', sans-serif; }

/* Size picker labels */
.ql-snow .ql-picker.ql-size .ql-picker-label::before,
.ql-snow .ql-picker.ql-size .ql-picker-item::before { content: '14' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="8px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="8px"]::before { content: '8' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before { content: '10' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before { content: '12' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before { content: '14' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before { content: '16' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before { content: '18' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="20px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="20px"]::before { content: '20' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before { content: '24' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="28px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="28px"]::before { content: '28' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="32px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="32px"]::before { content: '32' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before { content: '36' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before { content: '48' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="64px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="64px"]::before { content: '64' !important; }

.ql-snow .ql-picker.ql-size .ql-picker-label[data-value="72px"]::before,
.ql-snow .ql-picker.ql-size .ql-picker-item[data-value="72px"]::before { content: '72' !important; }

/* ===== Status Bar ===== */
.editor-status-bar {
  padding: 6px 16px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  font-size: 12px;
  color: #6b7280;
}

.editor-status-bar .btn-link {
  font-size: 14px;
  text-decoration: none;
}

/* ===== Scrollbar Styling ===== */
.document-canvas::-webkit-scrollbar {
  width: 10px;
}

.document-canvas::-webkit-scrollbar-track {
  background: #e8e8e8;
}

.document-canvas::-webkit-scrollbar-thumb {
  background: #c0c0c0;
  border-radius: 5px;
}

.document-canvas::-webkit-scrollbar-thumb:hover {
  background: #a0a0a0;
}

/* ===== Quill Content Styles ===== */
.contract-editor-wrapper .ql-editor h1 { font-size: 2em; margin-bottom: 0.5em; }
.contract-editor-wrapper .ql-editor h2 { font-size: 1.5em; margin-bottom: 0.5em; }
.contract-editor-wrapper .ql-editor h3 { font-size: 1.17em; margin-bottom: 0.5em; }
.contract-editor-wrapper .ql-editor p { margin-bottom: 0.8em; }
.contract-editor-wrapper .ql-editor blockquote {
  border-left: 4px solid #1a73e8;
  padding-left: 16px;
  color: #555;
  margin: 1em 0;
}

/* ===== Print-friendly ===== */
@media print {
  .editor-toolbar-area,
  .editor-status-bar,
  .ql-toolbar {
    display: none !important;
  }
  .document-canvas {
    background: white;
    padding: 0;
    max-height: none;
  }
  .document-page {
    box-shadow: none;
    transform: none !important;
  }
}
</style>
